import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';

import { fetchOrgSampleWeddingStatus } from 'apollo/projects.query';
import APModal from 'components/Modal/APModal';
import { LandingContent } from 'components/LandingContent';
import upgradePlanImage from 'assets/images/account-plan-upgrade.jpeg';
import CreateQuickProject from './CreateQuickProject';
import ProjectsSettings from './ProjectSettings';
import CreateSampleProjectModal from './CreateSampleProject';
import { CreateCustomTemplateProject } from './CreateCustomTemplateProject';
import './styles.scss';

export function useProjectModal() {
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(null);
  const projectsLimit = useSelector((state) => state.projects.projectsLimit);

  const projectsLimitErrorOnClose = () => {
    if (projectsLimit?.isOwner) {
      history.push('/app/v2/account/billing');
    } else {
      setModalOpen(null);
    }
  };

  const projectsLimitErrorAction = () => {
    return (
      <button
        className="button"
        onClick={projectsLimitErrorOnClose}
      >
        {projectsLimit?.isOwner ? 'Upgrade subscription' : 'Got it'}
      </button>
    );
  };

  const projectsLimitErrorContent = () => {
    if (projectsLimit?.isOwner) {
      return (`Your current subscription limits you to ${projectsLimit.limit} projects.
                To create additional projects, upgrade your plan.`);
    }
    return (`To create additional projects,
                please contact the owner of your account to upgrade your plan.`);
  };

  const upgradePlanContent = () => (
    <>
      Designed by event pros, for event pros, Aisle Planner’s sales
      and project management tools are linked seamlessly together in one
      stylish, intuitive, and easy-to-use online environment. Build your
      processes and workflows, manage every document and detail, and collaborate
      with your team, your clients, and your vendor partners all in one place,
      all behind one login. Upgrade now and put the “Power of One” to work for
      your business today! Plans start at just $44.99/month. Learn more about
      all Aisle Planner has to offer{' '}
      <a
        target="_blank"
        rel="noreferrer"
        href="https://www.aisleplanner.com/solutions/tools-for-event-professionals"
      >
        here
      </a>{' '}
      .
    </>
  );

  return [(
    <>
      <ProjectModal
        open={modalOpen === 'project'}
        onClose={() => setModalOpen(null)}
      />
      <APModal
        open={modalOpen === 'upgrade'}
        onClose={() => setModalOpen(null)}
        name="upgrade-plan"
      >
        <div className="content-container content-container--narrow">
          <LandingContent
            action={() => {
              history.push('/app/v2/account/billing');
            }}
            title="Do more with Aisle Planner"
            description={upgradePlanContent()}
            actionButtonTitle="Upgrade now"
            heroImage={upgradePlanImage}
          />
        </div>
      </APModal>
      <APModal
        open={modalOpen === 'limit'}
        onClose={() => setModalOpen(null)}
        name="projects-limit-error"
        title="You've reached your project limit."
        actions={projectsLimitErrorAction()}
      >
        <div className="content-container content-container--narrow">
          <div>{projectsLimitErrorContent()}</div>
        </div>
      </APModal>
    </>
  ), setModalOpen];
}

const ProjectModal = ({ open, onClose, weddingId = null, updateWeddingChanges, pushNotification = null }) => {
  const [type, setType] = useState(-1);
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [openCreateSampleProject, setOpenCreateSampleProject] = useState(false);
  const [finishedWeddingId, setFinishedWeddingId] = useState();
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [canCreateSample, setCanCreateSample] = useState(false);

  const topRef = useRef(null);

  const goToProject = (id) => {
    const _id = id ?? finishedWeddingId;
    window.location.href = `/app/project/${_id}/dashboard`;
  };

  const projectCreatedCallback = (data) => {
    const { createWedding, customTemplate } = data;
    const { wedding } = createWedding || {};
    const { id } = wedding || {};

    if (!customTemplate) {
      goToProject(id);
    } else {
      setFinishedWeddingId(id);
      setShowLoadingModal(true);
    }
  };

  useEffect(() => {
    if (!open) {
      setType(-1);
      setShowProjectSettings(false);
    } else if (weddingId !== null) {
      setShowProjectSettings(true);
    }

    const getSampleWeddingStatus = async () => {
      try {
        const dataStatus = await fetchOrgSampleWeddingStatus();
        const { sampleWeddingStatus } = dataStatus.me.organization;
        setCanCreateSample(sampleWeddingStatus === 'available' || sampleWeddingStatus === 'preparing');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    };
    getSampleWeddingStatus();
  }, [open]);

  useEffect(() => {
    if (showProjectSettings) topRef.current?.scrollIntoView({ block: 'end' });
  }, [showProjectSettings]);

  const updateType = (value) => {
    setType(value);
  };

  const openCreateProject = () => {
    if (type === 0) {
      setOpenCreateSampleProject(true);
      onClose();
    } else {
      setShowProjectSettings(true);
    }
  };

  const title = weddingId ? 'Project Settings' : 'Create a new project.';
  const name = weddingId ? 'project-settings' : 'create-quick-project';

  return (
    <>
      <APModal
        open={open}
        title={title}
        name={name}
        className={clsx(
          showProjectSettings
            ? 'settings_project_modal__container'
            : 'create_quick_project_modal__container',
          canCreateSample && 'large',
        )}
        onClose={onClose}
      >
        {showProjectSettings ? (
          <ProjectsSettings
            type={type}
            weddingId={weddingId}
            updateWeddingChanges={updateWeddingChanges}
            pushNotification={pushNotification}
            projectCreatedCallback={projectCreatedCallback}
          />
        ) : (
          <CreateQuickProject
            type={type}
            updateType={updateType}
            openCreateProject={openCreateProject}
          />
        )}
      </APModal>
      <CreateSampleProjectModal
        open={openCreateSampleProject}
        onClose={() => {
          setOpenCreateSampleProject(false);
        }}
      />
      {showLoadingModal && (
        <CreateCustomTemplateProject
          show={showLoadingModal}
          goToProject={goToProject}
          projectId={finishedWeddingId}
        />
      )}
    </>
  );
};
ProjectModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  updateWeddingChanges: PropTypes.func,
};

ProjectModal.defaultProps = {};

export default ProjectModal;
